import React, { useEffect, useState } from 'react'
import { useText } from '../context/TextContext';
import { Form, useNavigate, useParams } from 'react-router-dom';
import { Customer, CustomerType } from '../models/shared/Customer';
import { FormCheck, FormControl, FormGroup, FormLabel, Spinner } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { useForm } from 'react-hook-form';
import { AdvancedButton } from '../components/AdvancedButton';
import { useFirestore } from 'reactfire';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { COLLECTIONS } from '../utils/shared/constants';
import { useOrganization } from '../context/OrganizationContext';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import { useFirebaseDoc } from '../hooks/firebaseHooks';
import useQueryParam from '../hooks/useQueryParams';
import { Deal } from '../models/shared/Deal';
import { validateEmail, isPhone } from '../utils/validations';
import { syncAccountingRecord } from '../utils/api';

interface Input {
    name: string;
    lastName: string;
    email: string;
    phone: string;
    idNumber: string;
    note: string;
}

export default function CustomerWritePage() {
    const text = useText();
    const { orgId, organization } = useOrganization();
    const { getToken } = useUser();
    const { customerId } = useParams<{ customerId: string }>()
    const [type, setType] = React.useState<CustomerType>('person');
    const { register, handleSubmit, setValue } = useForm<Input>();
    const navigate = useNavigate();
    const [processing, setProcessing] = React.useState(false);
    const firestore = useFirestore();
    const [loading, setLoading] = useState(!!customerId);
    const [customer] = useFirebaseDoc<Customer>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.CUSTOMERS}/${customerId}`);
    const { dealId, query } = useQueryParam();

    const isPerson = type === 'person';

    useEffect(() => {
        if (!customer) {
            if (query) {
                if (validateEmail(query)) {
                    setValue('email', query);
                    document.querySelector<HTMLInputElement>('input[name="email"]')?.focus();
                } else if (isPhone(query)) {
                    setValue('phone', query);
                    document.querySelector<HTMLInputElement>('input[name="phone"]')?.focus();
                } else {
                    const nameParts = query.split(/\s+/).filter(Boolean);
                    if (nameParts.length > 1) {
                        setValue('name', nameParts.slice(0, -1).join(' '));
                        setValue('lastName', nameParts[nameParts.length - 1]);
                    } else {
                        setValue('name', query);
                    }
                    document.querySelector<HTMLInputElement>('input[name="name"]')?.focus();
                }
            }

            return;
        }
        setType(customer.type);
        setValue('name', customer.name);
        setValue('lastName', customer.lastName);
        setValue('email', customer.email);
        setValue('phone', customer.phone);
        setValue('idNumber', customer.idNumber);
        setValue('note', customer.note);

        setLoading(false);
    }, [customer, setValue, query])


    const onSubmit = async (data: Input) => {
        setProcessing(true);

        const newCustomer: Customer = {
            type,
            name: data.name || '',
            lastName: data.lastName || '',
            email: (data.email || '').toLowerCase(),
            phone: data.phone || '',
            idNumber: data.idNumber || '',
            note: data.note || '',
            status: 'active',
        }

        try {
            const colPath = `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.CUSTOMERS}`;
            let savedCustomerId = customerId;
            if (customerId) {
                await setDoc(doc(firestore, colPath, customerId), newCustomer, { merge: true });
            } else {
                const docRef = await addDoc(collection(firestore, colPath), newCustomer);
                savedCustomerId = docRef.id;

                if (dealId) {
                    const dealUpdate: Partial<Deal> = {
                        customerId: docRef.id,
                        brandNew: false
                    }
                    await setDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}`), dealUpdate, { merge: true });
                }
            }

            if (savedCustomerId && organization.integrations?.accountingType) {
                try {
                    const token = await getToken();
                    await syncAccountingRecord(token, orgId, 'customer', savedCustomerId, 'out');
                } catch (e) {
                    console.error('Failed to sync customer to accounting system', e);
                }
            }

            navigate(-1);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }

        setProcessing(false);
    }

    return (<>
        <PageHeader showBackButton title={customerId ? text.editCustomer : text.newCustomer} />

        {loading
            ? <Spinner />
            : <Form className='d-flex flex-column gap-4' onSubmit={handleSubmit(onSubmit)}>
                <FormGroup className='d-flex gap-4'>
                    <FormCheck type="radio" label={text.person} checked={type === 'person'} onChange={() => setType('person')} />
                    <FormCheck type="radio" label={text.company} checked={type === 'company'} onChange={() => setType('company')} />
                </FormGroup>

                <div className='d-flex gap-4 flex-wrap'>
                    <FormGroup className='flex-grow-1' >
                        <FormLabel>{isPerson ? text.firstName : text.name}</FormLabel>
                        <FormControl {...register('name')} />
                    </FormGroup>

                    {isPerson && <FormGroup className='flex-grow-1' >
                        <FormLabel>{text.lastName}</FormLabel>
                        <FormControl {...register('lastName')} />
                    </FormGroup>}
                </div>

                <FormGroup>
                    <FormLabel>{isPerson ? text.personalNumber : text.organizationNumber}</FormLabel>
                    <FormControl {...register('idNumber')} />
                </FormGroup>

                <hr />

                <div className='d-flex gap-4 flex-wrap'>
                    <FormGroup className='flex-grow-1'>
                        <FormLabel>{text.email}</FormLabel>
                        <FormControl type='email' {...register('email')} />
                    </FormGroup>

                    <FormGroup className='flex-grow-1'>
                        <FormLabel>{text.phone}</FormLabel>
                        <FormControl type='phone' {...register('phone')} />
                    </FormGroup>
                </div>

                <hr />

                <FormGroup>
                    <FormLabel>{text.note}</FormLabel>
                    <FormControl as='textarea' {...register('note')} />
                </FormGroup>

                <div className='ms-auto d-flex gap-4'>
                    <AdvancedButton icon='bi-x' variant='secondary' onClick={() => navigate(-1)}>{text.cancel}</AdvancedButton>
                    <AdvancedButton processing={processing} type='submit' icon='bi-floppy'> {customerId ? text.save : text.create}</AdvancedButton>
                </div>
            </Form>
        }
    </>
    )
}
